import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { articleContents } from '@/mocks/articles';

function buildArticleJsonLd(article: typeof articleContents[string], slug: string) {
  const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://kakobuypolska.buzz';
  const firstP = article.content.find(b => b.type === 'p');
  const description = firstP?.text ? firstP.text.substring(0, 160) : article.title;

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: description,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      '@type': 'Organization',
      name: 'Kakobuy Spreadsheet Polska',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Kakobuy Spreadsheet Polska',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png',
        width: 512,
        height: 512,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/artykul/${slug}`,
    },
    inLanguage: 'pl-PL',
    isAccessibleForFree: true,
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleContents[slug] : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background-50 px-4">
        <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">Artykuł nie został znaleziony</h1>
        <p className="text-foreground-600 mb-6">Przepraszamy, ale szukany artykuł nie istnieje.</p>
        <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full text-sm transition-all cursor-pointer whitespace-nowrap">
          <i className="ri-arrow-left-line"></i> Powrót na stronę główną
        </Link>
      </div>
    );
  }

  const jsonLd = buildArticleJsonLd(article, slug!);

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <nav className="sticky top-0 z-40 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-4xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <span className="font-heading text-lg font-bold text-foreground-900 whitespace-nowrap">
              Kakobuy<span className="text-accent-500">Spreadsheet</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/artykul/kakobuy-spreadsheet-poradnik"
              className="text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer hidden sm:inline-flex items-center gap-1"
            >
              <i className="ri-book-3-line"></i> Poradnik
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i> Strona główna
            </Link>
          </div>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-16">
        <div className="w-full h-48 md:h-64 rounded-xl overflow-hidden mb-8">
          <img
            src="https://readdy.ai/api/search-image?query=Clean%20modern%20blog%20header%20image%20with%20laptop%20displaying%20shopping%20spreadsheet%20on%20wooden%20desk%20with%20warm%20coffee%20and%20minimal%20decor%2C%20bright%20airy%20workspace%2C%20editorial%20photography%2C%20soft%20natural%20lighting&width=900&height=400&seq=article-detail-01&orientation=landscape"
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>

        <header className="mb-10">
          <div className="flex items-center gap-3 text-xs text-foreground-500 mb-4">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i> {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i> {article.readTime}
            </span>
          </div>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight">
            {article.title}
          </h1>
        </header>

        <div className="prose-custom">
          {article.content.map((block, i) => {
            if (block.type === 'h2') {
              return (
                <h2 key={i} className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
                  {block.text}
                </h2>
              );
            }
            if (block.type === 'h3') {
              return (
                <h3 key={i} className="font-heading text-lg font-semibold text-foreground-800 mt-8 mb-3">
                  {block.text}
                </h3>
              );
            }
            if (block.type === 'p') {
              return (
                <p key={i} className="text-foreground-700 leading-relaxed mb-4 text-sm md:text-base">
                  {block.text}
                </p>
              );
            }
            if (block.type === 'ul' && block.items) {
              return (
                <ul key={i} className="list-disc list-inside space-y-2 mb-4 text-sm md:text-base text-foreground-700">
                  {block.items.map((item, j) => (
                    <li key={j}>{item}</li>
                  ))}
                </ul>
              );
            }
            if (block.type === 'ol' && block.items) {
              return (
                <ol key={i} className="list-decimal list-inside space-y-2 mb-4 text-sm md:text-base text-foreground-700">
                  {block.items.map((item, j) => (
                    <li key={j}>{item}</li>
                  ))}
                </ol>
              );
            }
            if (block.type === 'table' && block.headers && block.rows) {
              return (
                <div key={i} className="overflow-x-auto rounded-xl border border-background-200/70 mb-6">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-secondary-100/80">
                        {block.headers.map((h, j) => (
                          <th key={j} className="text-left px-4 py-3 font-semibold text-foreground-800 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {block.rows.map((row, j) => (
                        <tr key={j} className="border-t border-background-200/70">
                          {row.map((cell, k) => (
                            <td key={k} className="px-4 py-3 text-foreground-600">{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            }
            return null;
          })}
        </div>

        <div className="mt-12 p-6 rounded-xl bg-primary-50 border border-primary-200">
          <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
            Chcesz zobaczyć pełną bazę produktów?
          </h3>
          <p className="text-sm text-foreground-600 mb-4">
            Przejdź do Kakobuy Spreadsheet i odkryj tysiące sprawdzonych produktów w jednym miejscu.
          </p>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full text-sm transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-shopping-cart-2-line"></i> Kakobuy Spreadsheet
          </a>
        </div>

        <div className="mt-8 p-5 rounded-xl bg-background-100 border border-background-200/70">
          <h4 className="font-heading text-sm font-semibold text-foreground-800 mb-3">Powiązane artykuły:</h4>
          <div className="flex flex-wrap gap-2">
            <Link to="/artykul/czym-jest-kakobuy-spreadsheet" className="text-xs text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap">Czym jest Kakobuy Spreadsheet?</Link>
            <Link to="/artykul/kakobuy-wysylka-polska" className="text-xs text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap">Wysyłka do Polski</Link>
            <Link to="/artykul/kakobuy-bezpieczenstwo" className="text-xs text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap">Bezpieczeństwo</Link>
            <Link to="/artykul/kakobuy-spreadsheet-sneakers" className="text-xs text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap">Sneakers</Link>
            <Link to="/artykul/kakobuy-spreadsheet-poradnik" className="text-xs text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap">Poradnik zakupowy</Link>
          </div>
        </div>

        <nav className="mt-10 pt-8 border-t border-background-200/70 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i> Powrót na stronę główną
          </Link>
          <span className="hidden sm:inline text-foreground-400">•</span>
          <Link
            to="/#artykuly"
            className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer"
          >
            Więcej artykułów <i className="ri-arrow-right-line"></i>
          </Link>
        </nav>
      </article>

      <footer className="bg-secondary-900 text-white py-8">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <p className="text-xs text-white/40">
            © 2026 Kakobuy Spreadsheet Polska. Wszelkie prawa zastrzeżone.
          </p>
        </div>
      </footer>
    </div>
  );
}